import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  addDoc, 
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';


class TeacherBatchSyncService {
  constructor() {
    this.listeners = new Map();
  }

  // Sync teacher data with batches
  async syncTeacherBatches(teacherId, batchIds, coachingId) {
    try {
      const batch = writeBatch(db);
      
      // Get teacher data
      const teacherDoc = await getDoc(doc(db, 'users', teacherId));
      if (!teacherDoc.exists()) {
        throw new Error('Teacher not found');
      }
      
      const teacherData = teacherDoc.data();
      
      // Update teacher's batch assignments
      const currentBatches = teacherData.teacherProfile?.batches || [];
      const updatedBatches = [...currentBatches];
      
      // Add new batches
      for (const batchId of batchIds) {
        if (!updatedBatches.find(b => b.id === batchId)) {
          const batchDoc = await getDoc(doc(db, 'batches', batchId));
          if (batchDoc.exists()) {
            const batchData = batchDoc.data();
            updatedBatches.push({
              id: batchId,
              name: batchData.name,
              subject: batchData.subject,
              assignedAt: new Date(),
              students: batchData.students || []
            });
          }
        }
      }
      
      // Update teacher document
      batch.update(doc(db, 'users', teacherId), {
        'teacherProfile.batches': updatedBatches,
        updatedAt: serverTimestamp()
      });
      
      // Update batch documents with teacher info
      for (const batchId of batchIds) {
        batch.update(doc(db, 'batches', batchId), {
          teacherId: teacherId,
          teacherName: teacherData.displayName,
          teacherEmail: teacherData.email,
          teacherSubject: teacherData.teacherProfile?.subject,
          updatedAt: serverTimestamp()
        });
      }
      
      await batch.commit();
      
      // Create teacher dashboard data
      await this.createTeacherDashboardData(teacherId, updatedBatches);
      
      return { success: true, message: 'Teacher-batch sync completed' };
    } catch (error) {
      throw error;
    }
  }

  // Create comprehensive teacher dashboard data
  async createTeacherDashboardData(teacherId, batches) {
    try {
      // Get real batch data from Firestore
      const batchesWithStats = await Promise.all(
        batches.map(async (batch) => {
          try {
            // Get actual batch document
            const batchDoc = await getDoc(doc(db, 'batches', batch.id));
            const batchData = batchDoc.exists() ? batchDoc.data() : {};
            
            // Get students for this batch
            const students = await this.getBatchStudents(batch.id);
            
            // Get real attendance data
            const attendanceRate = await this.getBatchAttendanceRate(batch.id);
            
            // Get pending doubts
            const pendingDoubts = await this.getBatchPendingDoubts(batch.id);
            
            // Get upcoming classes
            const upcomingClasses = await this.getBatchUpcomingClasses(batch.id);
            
            return {
              ...batch,
              ...batchData,
              stats: {
                totalStudents: students.length,
                attendanceRate: attendanceRate,
                averageScore: await this.getBatchAverageScore(batch.id),
                pendingAssignments: await this.getBatchPendingAssignments(batch.id),
                upcomingClasses: upcomingClasses.length,
                pendingDoubts: pendingDoubts.length
              }
            };
          } catch (error) {
            return {
              ...batch,
              stats: {
                totalStudents: 0,
                attendanceRate: 0,
                averageScore: 0,
                pendingAssignments: 0,
                upcomingClasses: 0,
                pendingDoubts: 0
              }
            };
          }
        })
      );

      const dashboardData = {
        teacherId,
        batches: batchesWithStats,
        performance: {
          totalStudents: batchesWithStats.reduce((sum, batch) => sum + (batch.stats?.totalStudents || 0), 0),
          totalBatches: batchesWithStats.length,
          averageAttendance: batchesWithStats.reduce((sum, batch) => sum + (batch.stats?.attendanceRate || 0), 0) / (batchesWithStats.length || 1),
          totalPendingDoubts: batchesWithStats.reduce((sum, batch) => sum + (batch.stats?.pendingDoubts || 0), 0),
          totalUpcomingClasses: batchesWithStats.reduce((sum, batch) => sum + (batch.stats?.upcomingClasses || 0), 0)
        },
        schedule: {
          todayClasses: await this.getTeacherTodayClasses(teacherId),
          weekClasses: await this.getTeacherWeekClasses(teacherId),
          upcomingEvents: await this.getTeacherUpcomingEvents(teacherId)
        },
        notifications: {
          pendingDoubts: batchesWithStats.reduce((sum, batch) => sum + (batch.stats?.pendingDoubts || 0), 0),
          newAssignments: await this.getTeacherNewAssignments(teacherId),
          attendanceAlerts: await this.getTeacherAttendanceAlerts(teacherId)
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Check if dashboard data exists
      const dashboardQuery = query(
        collection(db, 'teacherDashboards'),
        where('teacherId', '==', teacherId)
      );
      
      const dashboardSnapshot = await getDocs(dashboardQuery);
      
      if (dashboardSnapshot.empty) {
        await addDoc(collection(db, 'teacherDashboards'), dashboardData);
      } else {
        const dashboardDoc = dashboardSnapshot.docs[0];
        await updateDoc(doc(db, 'teacherDashboards', dashboardDoc.id), {
          ...dashboardData,
          createdAt: dashboardDoc.data().createdAt // Preserve creation date
        });
      }
      
      return dashboardData;
      
    } catch (error) {
      throw error;
    }
  }

  // Remove teacher from batch
  async removeTeacherFromBatch(teacherId, batchId) {
    try {
      const batch = writeBatch(db);
      
      // Update teacher document
      const teacherDoc = await getDoc(doc(db, 'users', teacherId));
      if (teacherDoc.exists()) {
        const teacherData = teacherDoc.data();
        const updatedBatches = teacherData.teacherProfile?.batches?.filter(b => b.id !== batchId) || [];
        
        batch.update(doc(db, 'users', teacherId), {
          'teacherProfile.batches': updatedBatches,
          updatedAt: serverTimestamp()
        });
      }
      
      // Update batch document
      batch.update(doc(db, 'batches', batchId), {
        teacherId: null,
        teacherName: null,
        teacherEmail: null,
        teacherSubject: null,
        updatedAt: serverTimestamp()
      });
      
      await batch.commit();
      
      return { success: true, message: 'Teacher removed from batch' };
    } catch (error) {
      throw error;
    }
  }

  // Get teacher's batch data with real-time updates
  subscribeToTeacherBatches(teacherId, callback) {
    // Subscribe to batches where teacherIds array contains the teacher
    const batchesQuery = query(
      collection(db, 'batches'),
      where('teacherIds', 'array-contains', teacherId)
    );
    
    const unsubscribe = onSnapshot(
      batchesQuery,
      (snapshot) => {
        const batchesList = [];
        snapshot.forEach((doc) => {
          const batchData = doc.data();
          batchesList.push({
            id: doc.id,
            ...batchData
          });
        });
        
        console.log('Real-time batches update:', batchesList.length, 'batches');
        callback(batchesList);
      },
      (error) => {
        console.error('Error in real-time batch subscription:', error);
        callback([]);
      }
    );
    
    this.listeners.set(teacherId, unsubscribe);
    return unsubscribe;
  }

  // Get batch document by ID with real student count
  async getBatchById(batchId) {
    try {
      const batchDoc = await getDoc(doc(db, 'batches', batchId));
      if (batchDoc.exists()) {
        const batchData = batchDoc.data();
        
        // Get real student count using same logic as TeacherBatchManagement
        let studentCount = 0;
        try {
          // First try to get all enrollments (not just active ones)
          const enrollmentsQuery = query(
            collection(db, 'batchEnrollments'),
            where('batchId', '==', batchId)
          );
          const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
          studentCount = enrollmentsSnapshot.size;
          
          // If no enrollments found, fallback to getBatchStudents
          if (studentCount === 0) {
            const students = await this.getBatchStudents(batchId);
            studentCount = students.length;
          }
        } catch (error) {
          console.error('Error getting student count for batch:', batchId, error);
          studentCount = 0;
        }
        
        return {
          id: batchDoc.id,
          ...batchData,
          // Use real student count from enrollments
          students: [], // We'll populate this if needed
          totalStudents: studentCount,
          activeStudents: studentCount,
          studentCount: studentCount // Add this for easy access
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting batch by ID:', error);
      return null;
    }
  }

  // Get batch students for teacher using batchEnrollments collection
  async getBatchStudents(batchId) {
    try {
      // Query batchEnrollments collection to get actual enrolled students
      const enrollmentsQuery = query(
        collection(db, 'batchEnrollments'),
        where('batchId', '==', batchId),
        where('status', '==', 'active') // Only active enrollments
      );
      
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      const students = [];
      
      for (const enrollmentDoc of enrollmentsSnapshot.docs) {
        const enrollmentData = enrollmentDoc.data();
        
        try {
          // Get user details for each enrollment
          const userDoc = await getDoc(doc(db, 'users', enrollmentData.userId));
          if (userDoc.exists()) {
            students.push({
              id: enrollmentData.userId,
              enrollmentId: enrollmentDoc.id,
              ...userDoc.data(),
              enrollmentData: enrollmentData
            });
          }
        } catch (error) {
          // If user not found, still count the enrollment
          students.push({
            id: enrollmentData.userId,
            enrollmentId: enrollmentDoc.id,
            name: `Student ${enrollmentData.userId}`,
            email: enrollmentData.userEmail || 'Unknown',
            enrollmentData: enrollmentData
          });
        }
      }
      
      return students;
    } catch (error) {
      console.error('Error loading batch students:', error);
      return [];
    }
  }



  // Get batch student count (faster method without loading full user data)
  async getBatchStudentCount(batchId) {
    try {
      const enrollmentsQuery = query(
        collection(db, 'batchEnrollments'),
        where('batchId', '==', batchId),
        where('status', '==', 'active')
      );
      
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      return enrollmentsSnapshot.size;
    } catch (error) {
      console.error('Error getting batch student count:', error);
      return 0;
    }
  }

  // Create settlement record for teacher
  async createTeacherSettlement(teacherId, settlementData) {
    try {
      const settlement = {
        teacherId,
        ...settlementData,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'teacherSettlements'), settlement);
      
      // Update teacher's settlement history
      const teacherDoc = await getDoc(doc(db, 'users', teacherId));
      if (teacherDoc.exists()) {
        const teacherData = teacherDoc.data();
        const settlements = teacherData.teacherProfile?.settlements || [];
        settlements.push({
          id: docRef.id,
          amount: settlementData.amount,
          period: settlementData.period,
          status: 'pending',
          createdAt: new Date()
        });
        
        await updateDoc(doc(db, 'users', teacherId), {
          'teacherProfile.settlements': settlements,
          updatedAt: serverTimestamp()
        });
      }
      
      return { success: true, settlementId: docRef.id };
    } catch (error) {
      throw error;
    }
  }

  // Get teacher performance analytics
  async getTeacherAnalytics(teacherId, timeRange = '30') {
    try {
      const teacherDoc = await getDoc(doc(db, 'users', teacherId));
      if (!teacherDoc.exists()) {
        throw new Error('Teacher not found');
      }
      
      const teacherData = teacherDoc.data();
      const batches = teacherData.teacherProfile?.batches || [];
      
      // Calculate analytics
      const analytics = {
        totalBatches: batches.length,
        totalStudents: batches.reduce((sum, batch) => sum + (batch.students?.length || 0), 0),
        averageAttendance: 0,
        classesCompleted: 0,
        assignmentsGiven: 0,
        studentFeedback: 0,
        performance: {
          attendance: [],
          scores: [],
          engagement: []
        }
      };
      
      // Get detailed analytics from other collections
      // This would involve querying attendance, assignments, etc.
      
      return analytics;
    } catch (error) {
      throw error;
    }
  }

  // Get batch attendance rate
  async getBatchAttendanceRate(batchId) {
    try {
      // Query attendance records for this batch from batchAttendance collection
      const attendanceQuery = query(
        collection(db, 'batchAttendance'),
        where('batchId', '==', batchId)
      );
      
      const attendanceSnapshot = await getDocs(attendanceQuery);
      
      if (attendanceSnapshot.empty) return 0;
      
      let totalRecords = 0;
      let presentCount = 0;
      
      attendanceSnapshot.forEach(doc => {
        const data = doc.data();
        totalRecords++;
        if (data.status === 'present') {
          presentCount++;
        }
      });
      
      return totalRecords > 0 ? Math.round((presentCount / totalRecords) * 100) : 0;
    } catch (error) {
      console.error('Error getting attendance rate:', error);
      return 0;
    }
  }

  // Get batch average score
  async getBatchAverageScore(batchId) {
    try {
      const scoresQuery = query(
        collection(db, 'testResults'),
        where('batchId', '==', batchId)
      );
      
      const scoresSnapshot = await getDocs(scoresQuery);
      
      if (scoresSnapshot.empty) return 0;
      
      let totalPercentage = 0;
      let count = 0;
      
      scoresSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.score !== undefined && data.maxMarks && data.maxMarks > 0) {
          const percentage = (data.score / data.maxMarks) * 100;
          totalPercentage += percentage;
          count++;
        } else if (data.percentage !== undefined) {
          totalPercentage += data.percentage;
          count++;
        }
      });
      
      return count > 0 ? Math.round(totalPercentage / count) : 0;
    } catch (error) {
      console.error('Error getting average score:', error);
      return 0;
    }
  }

  // Get batch pending doubts
  async getBatchPendingDoubts(batchId) {
    try {
      const doubtsQuery = query(
        collection(db, 'doubts'),
        where('batchId', '==', batchId)
      );
      
      const doubtsSnapshot = await getDocs(doubtsQuery);
      return doubtsSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        askedDate: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt) || new Date(),
        replies: doc.data().replies || [],
        likes: doc.data().likes || 0,
        isStarred: doc.data().isStarred || false
      }));
    } catch (error) {
      return [];
    }
  }

  // Get all doubts for batch (including answered ones)
  async getBatchAllDoubts(batchId) {
    try {
      const doubtsQuery = query(
        collection(db, 'doubts'),
        where('batchId', '==', batchId),
        orderBy('createdAt', 'desc')
      );
      
      const doubtsSnapshot = await getDocs(doubtsQuery);
      return doubtsSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        askedDate: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt) || new Date(),
        replies: doc.data().replies || [],
        likes: doc.data().likes || 0,
        isStarred: doc.data().isStarred || false
      }));
    } catch (error) {
      return [];
    }
  }

  // Get batch pending assignments
  async getBatchPendingAssignments(batchId) {
    try {
      const assignmentsQuery = query(
        collection(db, 'batchAssignments'),
        where('batchId', '==', batchId),
        where('status', '==', 'active')
      );
      
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      return assignmentsSnapshot.size;
    } catch (error) {
      return 0;
    }
  }

  // Get batch upcoming classes
  async getBatchUpcomingClasses(batchId) {
    try {
      const now = new Date();
      const today = now.toISOString().split('T')[0]; // YYYY-MM-DD format
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const nextWeekDate = nextWeek.toISOString().split('T')[0];
      
      // Try with scheduledAt field first
      try {
        const classesQuery = query(
          collection(db, 'batchClasses'),
          where('batchId', '==', batchId),
          where('scheduledAt', '>=', now),
          where('scheduledAt', '<=', nextWeek)
        );
        
        const classesSnapshot = await getDocs(classesQuery);
        if (!classesSnapshot.empty) {
          return classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }
      } catch (scheduledAtError) {
        // scheduledAt field might not exist, try with date field
      }
      
      // Fallback: Query by date field
      const classesQuery = query(
        collection(db, 'batchClasses'),
        where('batchId', '==', batchId),
        where('date', '>=', today),
        where('date', '<=', nextWeekDate)
      );
      
      const classesSnapshot = await getDocs(classesQuery);
      return classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting upcoming classes:', error);
      return [];
    }
  }

  // Get teacher's today classes
  async getTeacherTodayClasses(teacherId) {
    try {
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      
      const classesQuery = query(
        collection(db, 'batchClasses'),
        where('teacherId', '==', teacherId),
        where('scheduledAt', '>=', startOfDay),
        where('scheduledAt', '<', endOfDay)
      );
      
      const classesSnapshot = await getDocs(classesQuery);
      return classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      return [];
    }
  }

  // Get teacher's week classes
  async getTeacherWeekClasses(teacherId) {
    try {
      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      const classesQuery = query(
        collection(db, 'batchClasses'),
        where('teacherId', '==', teacherId),
        where('scheduledAt', '>=', now),
        where('scheduledAt', '<=', nextWeek)
      );
      
      const classesSnapshot = await getDocs(classesQuery);
      return classesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      return [];
    }
  }

  // Get teacher upcoming events
  async getTeacherUpcomingEvents(teacherId) {
    try {
      const eventsQuery = query(
        collection(db, 'teacherEvents'),
        where('teacherId', '==', teacherId),
        where('eventDate', '>=', new Date())
      );
      
      const eventsSnapshot = await getDocs(eventsQuery);
      return eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      return [];
    }
  }

  // Get teacher new assignments
  async getTeacherNewAssignments(teacherId) {
    try {
      const assignmentsQuery = query(
        collection(db, 'assignmentSubmissions'),
        where('teacherId', '==', teacherId),
        where('status', '==', 'submitted'),
        where('isNew', '==', true)
      );
      
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      return assignmentsSnapshot.size;
    } catch (error) {
      return 0;
    }
  }

  // Get teacher attendance alerts
  async getTeacherAttendanceAlerts(teacherId) {
    try {
      const alertsQuery = query(
        collection(db, 'attendanceAlerts'),
        where('teacherId', '==', teacherId),
        where('isResolved', '==', false)
      );
      
      const alertsSnapshot = await getDocs(alertsQuery);
      return alertsSnapshot.size;
    } catch (error) {
      return 0;
    }
  }

  // Get teacher dashboard data
  async getTeacherDashboardData(teacherId) {
    try {
      const dashboardQuery = query(
        collection(db, 'teacherDashboards'),
        where('teacherId', '==', teacherId)
      );
      
      const dashboardSnapshot = await getDocs(dashboardQuery);
      
      if (!dashboardSnapshot.empty) {
        const dashboardDoc = dashboardSnapshot.docs[0];
        return { id: dashboardDoc.id, ...dashboardDoc.data() };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  // Get recent activity for teacher
  async getTeacherRecentActivity(teacherId, maxResults = 10) {
    try {
      const activities = [];
      
      // Get recent doubts
      const doubtsQuery = query(
        collection(db, 'doubts'),
        where('teacherId', '==', teacherId),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      
      const doubtsSnapshot = await getDocs(doubtsQuery);
      doubtsSnapshot.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          type: 'doubt',
          message: `New doubt: ${data.title || 'Question asked'}`,
          time: data.createdAt?.toDate() || new Date(),
          batchId: data.batchId,
          studentName: data.studentName
        });
      });
      
      // Get recent submissions
      const submissionsQuery = query(
        collection(db, 'assignmentSubmissions'),
        where('teacherId', '==', teacherId),
        orderBy('submittedAt', 'desc'),
        limit(5)
      );
      
      const submissionsSnapshot = await getDocs(submissionsQuery);
      submissionsSnapshot.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          type: 'assignment',
          message: `Assignment submitted by ${data.studentName || 'Student'}`,
          time: data.submittedAt?.toDate() || new Date(),
          batchId: data.batchId,
          assignmentTitle: data.assignmentTitle
        });
      });
      
      // Sort by time and limit
      return activities
        .sort((a, b) => b.time - a.time)
        .slice(0, maxResults)
        .map(activity => ({
          ...activity,
          time: this.formatTimeAgo(activity.time)
        }));
      
    } catch (error) {
      return [];
    }
  }

  // Helper function to format time ago
  formatTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  }

  // Subscribe to individual batch changes
  subscribeToBatch(batchId, callback) {
    const unsubscribe = onSnapshot(
      doc(db, 'batches', batchId),
      (doc) => {
        if (doc.exists()) {
          callback({
            id: doc.id,
            ...doc.data()
          });
        } else {
          callback(null);
        }
      },
      (error) => {
        // Error listening to batch
      }
    );
    
    return unsubscribe;
  }

  // Subscribe to multiple batches
  subscribeToMultipleBatches(batchIds, callback) {
    const unsubscribes = [];
    const batchData = {};
    
    batchIds.forEach(batchId => {
      const unsubscribe = this.subscribeToBatch(batchId, (data) => {
        if (data) {
          batchData[batchId] = data;
        } else {
          delete batchData[batchId];
        }
        callback(Object.values(batchData));
      });
      unsubscribes.push(unsubscribe);
    });
    
    // Return cleanup function
    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }

  // Cleanup listeners
  cleanup() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
  }
}

export default new TeacherBatchSyncService();